import React, { useState, useEffect, useContext, createContext } from 'react';
import {
  FaBars,
  FaHome,
  FaUsers,
  FaBullhorn,
  FaLightbulb,
  FaBriefcase,
  FaSignOutAlt,
  FaChevronLeft,
  FaChartBar,
  FaComment,
  FaTag,
  FaDollarSign,
  FaBuilding,
  FaMap,
  FaVideo,
  FaBell,
} from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth, db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import MobileTabBar from './MobileTabBar';

const LayoutNestingContext = createContext(false);

const MOBILE_MQ = '(max-width: 768px)';

const Layout = ({ children }) => {
  const hasParentLayout = useContext(LayoutNestingContext);
  const isPrimaryLayout = !hasParentLayout;

  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(MOBILE_MQ).matches
  );
  const [isSidebarOpen, setIsSidebarOpen] = useState(
    () => typeof window !== 'undefined' && !window.matchMedia(MOBILE_MQ).matches
  );

  const [userName, setUserName] = useState('');
  const [lastLogin, setLastLogin] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_MQ);
    const sync = () => {
      const mobile = mq.matches;
      setIsMobile(mobile);
      if (mobile) {
        setIsSidebarOpen(false);
      } else {
        setIsSidebarOpen(true);
      }
    };
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    const fetchUserInfo = async () => {
      if (auth.currentUser) {
        const userDocRef = doc(db, 'users', auth.currentUser.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const data = userDoc.data();
          setUserName(data.name || data.displayName || auth.currentUser.email || '');
          if (data.lastLogin) {
            const date = data.lastLogin.toDate ? data.lastLogin.toDate() : new Date(data.lastLogin);
            setLastLogin(date.toLocaleString());
          }
        } else {
          setUserName(auth.currentUser.email || '');
        }
      }
    };
    fetchUserInfo();
  }, []);

  const handleLogout = async () => {
    try {
      await auth.signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleOverlayClick = () => {
    if (isMobile) {
      setIsSidebarOpen(false);
    }
  };

  const sidebarWide = isMobile ? true : isSidebarOpen;

  const menuItems = [
    { icon: <FaHome className="text-lg" />, label: 'Overview', path: '/dashboard' },
    { icon: <FaUsers className="text-lg" />, label: 'Users', path: '/users' },
    { icon: <FaBuilding className="text-lg" />, label: 'Organizations', path: '/organizations' },
    { icon: <FaBullhorn className="text-lg" />, label: 'Campaigns', path: '/campaigns' },
    { icon: <FaLightbulb className="text-lg" />, label: 'Takeovers', path: '/takeovers' },
    { icon: <FaBriefcase className="text-lg" />, label: 'Creatives', path: '/creatives' },
    { icon: <FaTag className="text-lg" />, label: 'Coupons', path: '/coupons' },
    { icon: <FaVideo className="text-lg" />, label: 'Share Your Story', path: '/ugc' },
    { icon: <FaBell className="text-lg" />, label: 'Scheduled Push', path: '/scheduled-push' },
    { icon: <FaDollarSign className="text-lg" />, label: 'Finances', path: '/finances' },
    { icon: <FaChartBar className="text-lg" />, label: 'Analytics', path: '/analytics' },
    { icon: <FaMap className="text-lg" />, label: 'Featured coverage', path: '/featured-signs-coverage' },
    { icon: <FaComment className="text-lg" />, label: 'iMessage', path: '/imessage' },
  ];

  return (
    <LayoutNestingContext.Provider value={true}>
      <div className="relative flex min-h-screen min-h-[100dvh] bg-slate-100 text-slate-900">
        {isPrimaryLayout && isMobile && (
          <header className="fixed left-0 right-0 top-0 z-[900] flex h-14 items-center gap-3 border-b border-slate-200/90 bg-white/90 pl-[max(1rem,env(safe-area-inset-left))] pr-4 pt-[env(safe-area-inset-top,0px)] backdrop-blur-md md:hidden">
            <button
              type="button"
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-slate-600 transition hover:bg-slate-100 hover:text-slate-900"
              onClick={() => setIsSidebarOpen(true)}
              aria-label="Open menu"
            >
              <FaBars className="text-xl" />
            </button>
            <img
              src="https://is1-ssl.mzstatic.com/image/thumb/Purple221/v4/cc/c6/12/ccc6128f-879c-aa6c-ce59-1f6732f6d152/AppIcon-0-0-1x_U007ephone-0-1-85-220.png/230x0w.webp"
              alt=""
              className="h-8 w-auto rounded-md"
            />
            <span className="truncate text-sm font-semibold tracking-tight text-slate-800">Vue Admin</span>
          </header>
        )}

        {isPrimaryLayout && isMobile && isSidebarOpen && (
          <button
            type="button"
            className="fixed inset-0 z-[998] bg-slate-900/50 backdrop-blur-sm md:hidden"
            aria-label="Close menu"
            onClick={handleOverlayClick}
          />
        )}

        {isPrimaryLayout && (
          <aside
            className={[
              'fixed inset-y-0 left-0 z-[1000] flex flex-col border-r border-slate-800/90 bg-slate-950 text-slate-300 shadow-2xl transition-[transform,width] duration-200 ease-out',
              isMobile ? 'w-[min(20rem,86vw)] pt-[env(safe-area-inset-top,0px)]' : '',
              isMobile && isSidebarOpen ? 'translate-x-0' : '',
              isMobile && !isSidebarOpen ? '-translate-x-full' : '',
              !isMobile ? 'md:translate-x-0' : '',
              !isMobile && isSidebarOpen ? 'md:w-64' : '',
              !isMobile && !isSidebarOpen ? 'md:w-[4.5rem]' : '',
            ].join(' ')}
          >
            <div
              className={`flex h-14 shrink-0 items-center border-b border-slate-800/90 px-3 ${
                sidebarWide ? 'justify-between gap-2' : 'justify-center'
              }`}
            >
              {(sidebarWide || isMobile) && (
                <img
                  src="https://is1-ssl.mzstatic.com/image/thumb/Purple221/v4/cc/c6/12/ccc6128f-879c-aa6c-ce59-1f6732f6d152/AppIcon-0-0-1x_U007ephone-0-1-85-220.png/230x0w.webp"
                  alt="Vue"
                  className="h-8 w-auto rounded-md opacity-95"
                />
              )}
              <button
                type="button"
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-800/80 hover:text-white"
                onClick={() => {
                  if (isMobile) {
                    setIsSidebarOpen(false);
                  } else {
                    setIsSidebarOpen((v) => !v);
                  }
                }}
                aria-label={isMobile ? 'Close sidebar' : isSidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
              >
                {isMobile || isSidebarOpen ? (
                  <FaChevronLeft className="text-lg" />
                ) : (
                  <FaBars className="text-lg" />
                )}
              </button>
            </div>

            <nav className="flex flex-1 flex-col gap-0.5 overflow-y-auto px-2 py-3">
              {menuItems.map((item, index) => {
                const onDashboard =
                  location.pathname === '/' ||
                  location.pathname === '/dashboard' ||
                  location.pathname.startsWith('/dashboard/');
                const active =
                  item.path === '/dashboard'
                    ? onDashboard
                    : location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);
                return (
                  <button
                    key={index}
                    type="button"
                    className={[
                      'flex w-full items-center rounded-lg py-2.5 text-left text-sm font-medium transition-colors',
                      sidebarWide ? 'gap-3 px-3' : 'justify-center px-0',
                      active
                        ? 'bg-slate-800 text-sky-400 shadow-inner shadow-black/20'
                        : 'text-slate-400 hover:bg-slate-800/60 hover:text-slate-100',
                    ].join(' ')}
                    onClick={() => {
                      const target = item.path === '/dashboard' ? '/dashboard' : item.path;
                      const onSection =
                        item.path === '/dashboard'
                          ? onDashboard
                          : location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);
                      if (!onSection) {
                        navigate(target);
                      }
                      if (isMobile) {
                        setTimeout(() => setIsSidebarOpen(false), 120);
                      }
                    }}
                  >
                    <span className="flex shrink-0 items-center justify-center">{item.icon}</span>
                    {sidebarWide && <span className="truncate">{item.label}</span>}
                  </button>
                );
              })}
            </nav>

            {sidebarWide && (
              <div className="mt-auto border-t border-slate-800/90">
                <div className="space-y-1 px-3 py-4">
                  {userName && (
                    <p className="truncate text-sm font-semibold text-slate-100">Welcome, {userName}</p>
                  )}
                  {lastLogin && <p className="text-xs text-slate-500">Last login: {lastLogin}</p>}
                </div>
                <button
                  type="button"
                  className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium text-rose-400 transition hover:bg-rose-950/50 hover:text-rose-300"
                  onClick={handleLogout}
                  aria-label="Logout"
                >
                  <span className="flex w-5 shrink-0 justify-center">
                    <FaSignOutAlt className="text-lg" />
                  </span>
                  <span>Logout</span>
                </button>
              </div>
            )}

            {!sidebarWide && !isMobile && (
              <div className="mt-auto border-t border-slate-800/90 p-2">
                <button
                  type="button"
                  className="flex w-full items-center justify-center rounded-lg py-2.5 text-rose-400 transition hover:bg-rose-950/40 hover:text-rose-300"
                  onClick={handleLogout}
                  aria-label="Logout"
                >
                  <FaSignOutAlt className="text-lg" />
                </button>
              </div>
            )}
          </aside>
        )}

        <main
          className={[
            'min-h-screen min-h-[100dvh] flex-1 bg-slate-100',
            isPrimaryLayout && isMobile
              ? 'pb-[calc(4.75rem+env(safe-area-inset-bottom,0px))] pl-[max(0px,env(safe-area-inset-left))] pr-[max(0px,env(safe-area-inset-right))] pt-[calc(3.5rem+env(safe-area-inset-top,0px))]'
              : '',
            isPrimaryLayout && !isMobile ? 'md:pl-6 md:pr-8 md:pt-6 md:pb-8' : '',
            isPrimaryLayout && !isMobile && isSidebarOpen ? 'md:ml-64' : '',
            isPrimaryLayout && !isMobile && !isSidebarOpen ? 'md:ml-[4.5rem]' : '',
          ].join(' ')}
        >
          {children}
        </main>

        {isPrimaryLayout && isMobile && <MobileTabBar />}
      </div>
    </LayoutNestingContext.Provider>
  );
};

export default Layout;
